<script setup lang="ts">
import { Color } from '@previo/codebooks'
import { PBtnType } from '../btn/types'
import { PSpaceSize } from '../space/types'
import PBtn from '../btn/PBtn.vue'
import PSpace from '../space/PSpace.vue'
import PTextTitle1 from '../typography/title/PTextTitle1.vue'
import { usePrevioDS } from '../../composables/helpers'
import PTextStandard from '../typography/body/PTextStandard.vue'

type Props = {
  image: string

  title?: null | string
  description?: null | string

  primaryBtn?: null | string
  primaryBtnIcon?: null | string
  secondaryBtn?: null | string
  secondaryBtnIcon?: null | string
}

type Emits = {
  (e: 'primary', evt: Event): void
  (e: 'secondary', evt: Event): void
}

defineProps<Props>()
const emits = defineEmits<Emits>()

const ds = usePrevioDS()
</script>

<template>
  <div class="ds-visual-empty-state p-text-center">
    <img :src="image" class="ds-visual-empty-state__image p-full-width" alt="" />
    <p-space :mobile="PSpaceSize.MD" vertical />

    <template v-if="title">
      <p-text-title1 :text="title" :color="Color.GREY_600" />
      <p-space :mobile="PSpaceSize.SUB_HEADLINE" vertical />
    </template>

    <p-text-standard v-if="description" :text="description" :color="Color.GREY_500" />

    <template v-if="primaryBtn || secondaryBtn">
      <p-space :mobile="PSpaceSize.LG" vertical />

      <div :class="{ 'p-flex p-items-center p-no-wrap': ds.screen().isLaptopAndLarge }">
        <p-btn
          v-if="primaryBtn"
          :text="primaryBtn"
          :icon-end="primaryBtnIcon"
          :color="Color.POSITIVE"
          class="p-laptop-and-large-order-3"
          cy="primary-btn"
          grow
          @click="(e: Event) => emits('primary', e)"
        />

        <p-space
          :mobile="PSpaceSize.XS"
          :vertical="ds.screen().isMobile"
          class="p-laptop-and-large-order-2"
        />

        <p-btn
          v-if="secondaryBtn"
          :type="PBtnType.OUTLINE"
          :text="secondaryBtn"
          :icon-end="secondaryBtnIcon"
          class="p-laptop-and-large-order-1"
          cy="secondary-btn"
          grow
          @click="(e: Event) => emits('secondary', e)"
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.ds-visual-empty-state {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 160px;
  max-width: 400px;
}

.ds-visual-empty-state__image {
  flex: 0 1 auto;
  min-height: 0;
  object-fit: contain;
}
</style>
